import { Money, formatMoney, toMoney } from '@betterment/core';
import { Box, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { DataPoint } from './components/DataPoint';

import { urlBuilder } from '../../../../Shared/urlBuilder';
import { EnvelopeAccountHeaderQuery } from '../graphql/envelopeAccountHeader.generated';

type CashAccountOverviewProps = {
  accountId: string;
  apy: Extract<EnvelopeAccountHeaderQuery['account'], { __typename: 'CashAccount' }>['apy'];
  balance: Money;
  fdicInsurance: Extract<EnvelopeAccountHeaderQuery['account'], { __typename: 'CashAccount' }>['fdicInsurance'];
  performanceTotals: Extract<EnvelopeAccountHeaderQuery['account'], { __typename: 'CashAccount' }>['performanceTotals'];
};

export const CashAccountOverview = ({
  accountId,
  apy,
  balance,
  fdicInsurance,
  performanceTotals,
}: CashAccountOverviewProps) => {
  const { interest } = performanceTotals;

  const performanceUrl = urlBuilder.appAccountPerformance.absolute({
    accountId,
    returnTo: urlBuilder.appAccount.absolute({ accountId }),
    returnLabel: 'Account details',
  });

  return (
    <Box css={{ display: 'grid', gap: 'space.300' }}>
      <Separator />
      <Box css={{ display: 'grid' }}>
        <Text>Balance</Text>
        <Text variant="text400">{formatMoney(balance)}</Text>
      </Box>
      <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
        <DataPoint
          label="Current APY*"
          value={`${apy.rate}%`}
          explanation={apy.explanation}
          isBoostedActive={apy.isBoostActive}
        />
        <DataPoint label="FDIC Insurance" value={fdicInsurance.amount} explanation={fdicInsurance.explanation} />
        <DataPoint
          label="Total interest paid"
          value={formatMoney((interest?.paid as Money) ?? toMoney(0))}
          valueColor={(interest?.paid ?? toMoney(0)).cents === 0 ? 'text' : 'text.success'}
        />
        <DataPoint
          label="Interest to be paid"
          value={formatMoney((performanceTotals.interest?.accrued as Money) ?? toMoney(0))}
          valueColor={(interest?.accrued ?? toMoney(0)).cents === 0 ? 'text' : 'text.success'}
          explanation={interest?.accruedExplanation}
        />
      </Box>
      <Separator />
      <SambaAutoLink to={performanceUrl}>See balance history</SambaAutoLink>
    </Box>
  );
};
